'use client'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

import { EASE, Eyebrow, HAIRLINE, useReveal } from '@/components/page-kit'
import { HRMS, HRMS_MODULES } from './data'

/* The bookend: the page opened on the lifecycle track running left to right,
   and closes on the same six steps with every marker filled. The closing
   headline is the authored tagline, so the page ends on the product's own
   words rather than a new claim. */

export function HrmsCta() {
  const track = useReveal<HTMLDivElement>(0.25)

  return (
    // No top margin: the image band above fades to transparent directly onto
    // this section's own circuit field.
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[70%]"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
          maskImage:
            'radial-gradient(ellipse 110% 76% at 50% 34%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
        }}
      />

      <div className="container relative z-10 mx-auto py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl">
          <Eyebrow>Ready when you are</Eyebrow>

          <h2 className="font-heading mt-6 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[54px] lg:leading-[1.1]">
            <ChromaText>{HRMS.tagline}</ChromaText>
          </h2>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {HRMS.description}
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Request a demo</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="/customer-stories">Read success stories</a>
            </Button>
          </div>
        </div>
      </div>

      {/* The track, closed out. */}
      <div ref={track.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
        <div className="grid grid-cols-2 gap-px bg-border/70 sm:grid-cols-3 lg:grid-cols-6">
          {HRMS_MODULES.map((m, i) => (
            <div
              key={m.name}
              className="flex items-center gap-2.5 bg-background px-4 py-5 sm:px-5"
              style={{
                opacity: track.shown ? 1 : 0,
                transition: `opacity 460ms ${EASE} ${i * 70}ms`,
              }}
            >
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full bg-emerald-600 dark:bg-primary-active"
              />
              <span className="font-heading text-sm font-normal leading-none text-foreground sm:text-base">
                {m.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
